/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What a destination accepts** — one declaration for every place a value can be *written to*
 * (`#1163`, `CUSTOM_FIELDS_DESIGN` `D34`).
 *
 * ## Sources and destinations are two catalogs
 *
 * `field-catalog.ts` answers *where can a value come from* — session concepts, ours and the
 * standards', global by nature, governed by `D20`'s three legs (offered · resolves · **written**).
 * This answers the opposite question: *what does this place accept* — a VAST query parameter, an
 * ORTB body path, a vendor's field. Its header said so before either was built:
 *
 * > *"`suppliedBy` and `importance` (`D27`). Those are properties of a **destination** catalog —
 * > where a value is written to — and this is the catalog of **sources** a value can be read from."*
 *
 * **A destination vocabulary is scoped to its destination by construction**, which is why a vendor's
 * fields need no scope mechanism to stay out of the shared session model (`D26`'s bound, satisfied
 * structurally rather than by a rule anyone has to remember). `pubid` is not a value the session can
 * produce; it is a slot Publica accepts.
 *
 * ## Why it is shared — three of these already existed, disagreeing
 *
 * Measured 2026-08-25: `VAST_PARAM_CATALOG` (name · importance · description · `defaultMacro`),
 * `ORTB_FIELD_CATALOG` (path · type · importance · description — **no default at all**) and
 * `AdServerPreset.parameterRules` (`required[]` · `optional[]` · `fieldMap{}`). Three shapes for one
 * concept, written one destination at a time, with **two `importance` enums whose members are
 * identical** — and `#1090` was about to add a fourth for vendor templates.
 *
 * Every one of them could say *this value comes from a macro* or *from our field of another name*,
 * and **none could say _only the operator has it_** — which is exactly why the endpoint needed
 * `hasUnfilledEndpointPlaceholder`, a validator written for one field because the catalogue it
 * belonged to could not express the class (`D32`).
 */

import type { WireRendering } from './wire-rendering';
import type { EvidenceGrade } from './value-provenance';
import { isOrtbBodyLeafNode } from './ortb-body-leaf';
import type { FindingKind } from './finding-kind';
import { catalogEntryFor, FIELD_CATALOG, protocolConceptFor, wireExpressionFor } from './field-catalog';
import type { FieldCatalogEntry, ValueConstraint } from './field-catalog';
/**
 * **The source half of `checkDestinationReference`, read through the declarations that own each
 * spelling** (`FD11`, `#1413`). `field-catalog.ts` is where *which concept is this path* belongs,
 * and it cannot hold the lookup: that file imports **nothing**, by a rule its own spec enforces,
 * and resolving `content.genres[0]` needs the bracket rule and the root aliases. Both files are
 * already in the aliased subgraph and import nothing themselves, so this costs the boundary
 * nothing. If a second caller ever wants the lookup, it moves to a file of its own rather than
 * being copied.
 */
import { canonicalizeContextPath } from './context-roots';
import { splitAuthoredPath } from './own-path';
import { macroTokenName } from './mapping-value-slots';
/**
 * **The lifetime vocabulary is one vocabulary** (`X32` Q2, `#1248`). *Sources and destinations are
 * two catalogs* governs *which paths have which lifetime*; how long a `break` lasts is neither
 * side's to redefine, and a second enum would be `X3` across the two catalogues. `context-roots.ts`
 * imports nothing, so this keeps the aliased subgraph closed.
 */
import type { FanOut, ValueLifetime } from './context-roots';
import { toDurationSeconds } from './duration-seconds';

/** How much a destination wants a field. One enum, replacing `ParamImportance` and `OrtbFieldImportance`. */
export type FieldImportance = 'required' | 'recommended' | 'optional';

/** The shape a destination expects, where it says. */
export type DestinationFieldType = 'string' | 'number' | 'boolean' | 'array' | 'object';

/**
 * **Where this field's value comes from** (`D32`) — the axis none of the three catalogs had.
 *
 * It is not `kind` (what shape the value takes on the wire) and not `importance` (how much this
 * destination wants it). It decides what **creation** does, and it is what makes paste-and-parse
 * deterministic: given a declaration, a pasted parameter has exactly one correct treatment.
 */
/** A vendor field's evidence grade and what it rests on (`D40`). */
export interface FieldEvidence {
  readonly grade: EvidenceGrade;
  /** Where it came from — a backup, a vendor document, a live tag — or, for `unverified`, why nothing was checked. */
  readonly basis: string;
}

export type DestinationValue =
  /** Resolved per request from a source concept — seed the mapping. `defaultMacro` was this, spelled once. */
  | { readonly from: 'derived'; readonly source: string }
  /** The destination states the value — seed it. A vendor constant such as `output=vast4`. */
  | { readonly from: 'declared'; readonly value: string }
  /**
   * Only the operator has it — an account id, a network id, an endpoint.
   *
   * Take what a pasted tag supplied; otherwise leave it **unfilled and warn that no value exists**.
   * A declaration cannot invent one, and inventing one is worse than leaving it blank: a blank is
   * obviously unfinished, while a plausible wrong value silently no-bids.
   */
  | { readonly from: 'operator' }
  /**
   * **A member of a vocabulary we own, rendered for whoever receives it** (`N11h`, #1112).
   *
   * The fourth answer to *where does this value come from*, and the one the other three cannot
   * give: the operator states a **meaning** and the field name and integer are decided by the
   * receiver. `video.placement` is `plcmt: 2` to a 2.6 exchange, unexpressible to a 2.5 one, and
   * `accompanying` to the person authoring it.
   *
   * It is `derived`'s mirror image, which is why it is a sibling rather than a flavour of it: a
   * `derived` field's value comes from the **session** and its wire form is fixed; a `concept`
   * field's value comes from the **operator** and its wire form is not.
   *
   * The vocabulary is {@link ProtocolConcept}, and the corollary's rule is what makes closing it
   * legitimate — *close over a vocabulary we own, never one somebody else evolves*. The members
   * are bidBrain's words; the integers they render to are the IAB's, and those live in the
   * declaration where each version can be checked against its own spec.
   */
  | { readonly from: 'concept'; readonly concept: string };

/**
 * **When this declaration applies** (`D35`).
 *
 * Deliberately **one term**. `P2 · Criteria` is the general form and this is a stand-in for it, so
 * the rule is the one `D31` put on template `domains`: the vocabulary **does not grow privately**.
 * A private match language written before Criteria would be written to be un-written; when Criteria
 * lands this widens as an *addition*, because the field and its evaluator already exist.
 */
export interface DestinationApplicability {
  /**
   * Bounds on the destination's own protocol version: `min` and `max` inclusive, `below` exclusive.
   *
   * Compared **numerically, part by part**, never against a closed union of known versions — the
   * corollary's rule, since the version vocabulary belongs to IAB rather than to us. `2.10` is
   * therefore later than `2.9`, which a string comparison would get wrong.
   *
   * **`below` exists because *until version X* cannot be said with an inclusive bound** (#1315). A
   * field that moved in 2.6 applies to every version before it, and `max: '2.5'` would claim 2.5 is
   * the last version before 2.6 — a closed list of IAB's versions, which a `2.5.1` falls outside.
   */
  readonly protocolVersion?: { readonly min?: string; readonly max?: string; readonly below?: string };
}

/** One field a destination accepts. */
export interface DestinationField {
  /**
   * How this destination addresses the field — a query parameter's name, a body path relative to
   * its section, a vendor's own spelling.
   *
   * Deliberately **not** called `path`: a source entry's `path` is a context path we resolve *from*,
   * and using one word for both is how the two catalogs got conflated in the first place.
   */
  readonly name: string;
  readonly importance: FieldImportance;
  /**
   * **Optional for the same reason `value` is.** A destination may declare a slot it cannot
   * describe — the vendor presets name 70 fields and describe none of them — and writing text for
   * a third party's field is a claim about that vendor, subject to the same rule as
   * `endpointTemplate`: state it where something verifies it, or leave it out.
   */
  readonly description?: string;
  /**
   * **Absent means the declaration says nothing about the value** — not that the operator supplies
   * it, which is a positive claim (`from: 'operator'`) and a different one.
   *
   * That distinction is the whole `#1045` rule applied to a type: most of `ORTB_FIELD_CATALOG`'s
   * fields say nothing about their value (the counts are asserted in `ortb-field-catalog.spec.ts`),
   * and recording that as *operator-supplied* would turn *we have not stated this* into *we have
   * decided this*, which is precisely the claim that would then be shown to an operator.
   */
  readonly value?: DestinationValue;
  readonly valueType?: DestinationFieldType;
  /**
   * **What makes a value at this slot wrong** (`D41`, `#1192`) — see {@link ValueConstraint}.
   *
   * This slot's *own* rule, as opposed to a concept's grammar, which is declared on the source
   * entry and reached through {@link DestinationField.value} rather than restated here. `plcmt`
   * accepting 1–4 is a fact about this receiver; `us_privacy` being a CCPA string is not.
   *
   * **It is checked independently of {@link DestinationField.valueType}** — see the note in
   * {@link checkDestinationValue}, where keying the whole sweep on `type` would have disabled it
   * on every field that declares no type, which today is all 70 of the vendor presets'.
   */
  readonly constraint?: ValueConstraint;
  /** How a structured value is rendered onto this destination (`D23` · `R4`). */
  readonly sendAs?: WireRendering;
  readonly when?: DestinationApplicability;
  /**
   * **The name this field had before it moved** (#1315). OpenRTB 2.6 moved seven signals out of
   * `ext` into their parent — `regs.ext.gdpr` became `regs.gdpr` — so one signal has one row per
   * version, each bounded by `when`, and the newer row names the older.
   *
   * Declared on the row rather than inferred from the `ext.` prefix: the link is a fact about two
   * specific fields, and a rule matching every `ext.X` beside an `X` would pair fields nobody paired.
   */
  readonly supersedes?: string;
  /**
   * **How well this declaration is known, and from what** (`D40`, `1f-iii`) — for a vendor's field,
   * where a type, description or importance may be a reasoned guess. Our own catalogs declare what the
   * standards say and carry none. The grade reaches the seeded row as its `template` origin, which is
   * where an operator sees it; `basis` stays here, for whoever next checks the field.
   */
  readonly evidence?: FieldEvidence;
  /**
   * **This slot is refused, and here is why** (`FD1`, `4b`). Present means *known, and not offered*.
   *
   * Promoted onto the shared type because it was declared once per catalog under one name for one
   * concept — the response targets' `notOffered` and nothing else, since no outbound catalog refuses
   * a slot yet. And the promotion has a second payment, which is `#1313`'s rule: *a declaration that
   * withholds something is a boundary only where the withheld thing is resolved*. The console's picker
   * and the resolver's refusal now read this one field, rather than two lists a spec held equal by
   * reading one of them out of source text.
   *
   * A refused slot is still a **known** slot, so a stored row writing it is described rather than
   * reported unknown — withholding it *and* calling it unknown reports our decision as the operator's
   * mistake.
   */
  readonly notOffered?: string;
  /**
   * **This slot reads its value by a format of its own, not by its type** (`#933`, `FD4`).
   *
   * `ad.durationSeconds` is a number, and a `<Duration>` arrives as `00:00:30`: the resolver converts
   * it, so a literal `00:00:30` written there is correct. Graded by {@link DestinationField.valueType}
   * alone it reads as *expects a number* — a surface warning about correct behaviour. So the format
   * is declared on the slot and both the resolver and {@link checkDestinationValue} read it, which is
   * what keeps them agreeing: it was the resolver's `TARGET_VALUE_RULES`, keyed by target name beside
   * the declaration, where the checker could not see it.
   *
   * **One member, because the audit found one** — `durationSeconds` is the only target whose values
   * arrive in a format that is not their type. A second is a member here, never a change to the shape.
   */
  readonly readAs?: DestinationValueFormat;
}

/** Formats a slot may read its value by — a vocabulary we own (see {@link DestinationField.readAs}). */
export type DestinationValueFormat = 'duration-seconds';

/**
 * **Read a value by its slot's declared format**, or `undefined` where the value is not one.
 *
 * The one reader for every consumer of `readAs` — the resolver writing an ad, the checker grading a
 * literal — so the two cannot disagree about which values the format accepts.
 */
export const DESTINATION_VALUE_READERS: Readonly<Record<DestinationValueFormat, (value: unknown) => unknown>> = {
  'duration-seconds': toDurationSeconds,
};

/** How each format is named to an operator, for the finding that says a value is not one. */
const DESTINATION_VALUE_FORMAT_PHRASE: Readonly<Record<DestinationValueFormat, string>> = {
  'duration-seconds': 'a duration — seconds, or hh:mm:ss, above zero',
};

/** Compare dotted numeric versions. `2.10` is later than `2.9`; a lexical compare says otherwise. */
function compareVersions(a: string, b: string): number {
  const left = a.split('.').map((p) => Number.parseInt(p, 10) || 0);
  const right = b.split('.').map((p) => Number.parseInt(p, 10) || 0);
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const diff = (left[i] ?? 0) - (right[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/**
 * Whether a declaration applies in this context — **the one evaluator** (`D35`).
 *
 * Nothing branches on version inline again: `VERSION_IMPORTANCE_OVERRIDES` was a second copy of part
 * of the ORTB field list keyed by version, and its own comment called future deltas *"a one-line
 * addition rather than a fork of the catalog"* while being a fork of the catalog, deferred.
 *
 * **An unstated context is not a mismatch.** A caller that does not know the protocol version gets
 * every declaration, because filtering on an unknown would hide fields rather than narrow them.
 *
 * **The parameter is structural, not `DestinationField`** (`FD10`, `4a`). A response *source* entry
 * carries the same `when` for the same reason — `bid.dur` is 2.6+ — and it is a
 * `MappingFieldDescriptor`, not a destination. Widening the parameter is what lets both read one
 * evaluator; narrowing it to the destination type is what produced the private `parseFloat`
 * comparator this replaces.
 */
export function appliesTo(
  field: { readonly when?: DestinationApplicability },
  context: { protocolVersion?: string } = {},
): boolean {
  const bounds = field.when?.protocolVersion;
  if (!bounds) return true;
  const version = context.protocolVersion?.trim();
  if (!version) return true;
  if (bounds.min && compareVersions(version, bounds.min) < 0) return false;
  if (bounds.max && compareVersions(version, bounds.max) > 0) return false;
  if (bounds.below && compareVersions(version, bounds.below) >= 0) return false;
  return true;
}

/**
 * What a newly added row starts with, or `undefined` when only the operator can say.
 *
 * A `derived` field seeds its macro — the `{name}` spelling, because that is what an authoring row
 * carries and what the resolver reads.
 */
export function seedValueFor(field: DestinationField): string | undefined {
  if (field.value?.from === 'derived') return `{${field.value.source}}`;
  if (field.value?.from === 'declared') return field.value.value;
  // **Seeded with the assumption that is actually in force**, not with a blank. The driver
  // already asserts this member on every request, so an empty box beside it would teach the
  // operator there is no default when there is — the resolution-mark rule, one surface over:
  // state what is in force, or say you do not know.
  if (field.value?.from === 'concept') return protocolConceptFor(field.value.concept)?.assumed;
  return undefined;
}

/* ------------------------------------------------------------------------- *
 * Required-ness — the claim `optional` was carrying on the side (`X46`)
 * ------------------------------------------------------------------------- */

/**
 * **What a mapping row says about how much its destination wants it** (`X46`, 2026-09-02).
 *
 * Raised by Todd against `X29`: *"omission and optional are different concepts and shouldn't be
 * conflated."* They were. `optional` is the **omission** answer — *when this resolves to nothing,
 * omit rather than send `foo=`* — and it was simultaneously read as *this destination will not work
 * without a value here* by four operator-facing surfaces that between them alter nothing on the
 * wire.
 *
 * > **The separating test: does changing this alter what goes on the wire?** Omission does, always.
 * > Required-ness never does — it only changes what we tell the operator.
 *
 * The clincher was the ORTB body, where `$optional` has **no wire meaning at all**: the driver
 * strips it and an unresolved key is omitted either way (#880). So one checkbox on one shared
 * control meant the wire on one surface and a declaration on another — *a surface may differ in
 * what it maps to, never in what a mapping is*.
 */
export interface StoredImportanceClaim {
  /** The row's own claim, since `X46`. Absent means it has not made one. */
  readonly importance?: FieldImportance;
  /**
   * **Pre-`X46` spelling, read at load and never written** — the `X3` legacy-read pattern.
   *
   * Only `false` carries anything: until `X46` that was the one way an operator could say *this is
   * required*, and a pre-pilot deployment has rows that mean it. `true`, or absent, is the default
   * that has always been there, so reading it as a decision would turn *nobody stated this* into
   * *we decided this* — the same asymmetry {@link DestinationField.value} keeps, in the other
   * direction.
   */
  readonly optional?: boolean;
}

/**
 * **The legacy read, and it belongs at the store boundary rather than at render.**
 *
 * Called **once per store, when a stored document becomes an editing row** — the same place
 * `ortbBodyLeafDefault` and `readMappingValueSlots` sit. Resolving `optional` at render instead
 * would keep the two claims fused on the one surface where `optional` is still a live control: an
 * operator unchecking *Optional* on a query parameter means *send `foo=`*, and it must not also be
 * heard as *this field is required*.
 */
export function readStoredImportance(stored: StoredImportanceClaim | undefined): FieldImportance | undefined {
  if (stored?.importance !== undefined) return stored.importance;
  if (stored?.optional === false) return 'required';
  return undefined;
}

/**
 * **The one required-ness resolution**: the row's own claim, else the destination's declaration,
 * else nothing stated.
 *
 * `undefined` is a real answer and is **not** `'optional'`. A destination that declares nothing —
 * which is most of `ORTB_FIELD_CATALOG` and all 70 of the vendor presets' fields — has said
 * nothing, and rendering that as *optional* would show an operator a decision nobody made.
 */
export function resolveFieldImportance(
  authored: FieldImportance | undefined,
  declared: FieldImportance | undefined,
): FieldImportance | undefined {
  return authored ?? declared;
}

/**
 * What to **store** when an operator sets the importance of a row the destination already declares.
 *
 * A claim identical to the declaration is stored as **nothing**, so *absent means they have not
 * made one* keeps meaning that — otherwise opening a row and leaving it alone would write a
 * decision nobody made, and the next catalogue correction could not reach the row.
 */
export function authoredImportanceToStore(
  next: FieldImportance | undefined,
  declared: FieldImportance | undefined,
): FieldImportance | undefined {
  return next === declared ? undefined : next;
}

/**
 * Does this destination need a value here?
 *
 * Takes the resolved importance rather than a row, so a caller cannot skip
 * {@link resolveFieldImportance} and re-derive the answer from `optional` — which is the fusion
 * `X46` closes.
 */
export function isRequiredHere(importance: FieldImportance | undefined): boolean {
  return importance === 'required';
}

/** True when the destination declares a slot it cannot fill — the class `hasUnfilledEndpointPlaceholder` was written for. */
export function needsOperatorValue(field: DestinationField): boolean {
  return field.value?.from === 'operator';
}

/**
 * The members of a concept field this receiver can actually express.
 *
 * **The operator-facing half of the omit-rather-than-approximate rule.** A 2.5 exchange has no
 * value meaning `accompanying`, so the driver leaves the field off and says so in a log — which
 * is *"a constraint enforced somewhere the operator cannot act on it"*, i.e. documentation rather
 * than validation (#1031). Offering only what this version can carry moves the constraint to the
 * one surface where a person can do something about it.
 *
 * Returns `[]` for a field that is not concept-valued, so a caller can ask without branching.
 */
export function conceptMembersFor(field: DestinationField, context: { protocolVersion?: string } = {}): string[] {
  if (field.value?.from !== 'concept') return [];
  const declared = protocolConceptFor(field.value.concept);
  if (!declared) return [];
  return declared.members.filter((m) => wireExpressionFor(declared.concept, m, context.protocolVersion) !== undefined);
}

// ---------------------------------------------------------------------------
// Does the literal match what this destination said it accepts? (`#1191`, `#914` tier 1)
// ---------------------------------------------------------------------------

/**
 * **How a destination carries a value**, which is what decides how strictly a literal is read.
 *
 * A query string has one representation for everything, so `1080` and `"1080"` are the same four
 * bytes on the wire and reading the literal as text that must *parse* is the honest check. A JSON
 * body carries the literal's own type to the receiver, so a quoted number is a quoted number — the
 * type/enum violation an exchange may reject, which is the same reason an unresolved macro drops
 * the key there and is left literal on a header (CLAUDE.md, macro resolution semantics).
 *
 * Passed by the caller rather than declared per field: every entry of one destination catalog has
 * the same answer, so putting it on the entry would be 120 copies of one fact. Required rather than
 * defaulted, so a new surface has to state which it is instead of inheriting somebody else's.
 */
export type DestinationEncoding = 'text' | 'json';

/**
 * **How strictly to read a literal that a row's `Type` will coerce** (`FD4`, `4b`).
 *
 * A typed surface — the ORTB body, the ad a response mapping writes — sends a literal's own type,
 * unless it is converted: the driver and the resolver both parse a default or a static value by
 * `runtimeCoercion` (the row's `Type`, else the field's own, else the expression's — `FD13`). So `"5"`
 * on a number field is right with no `Type` and wrong under `Type: string`, and grading it the same way
 * in both cases would warn about correct behaviour in one of them. A `string` coercion keeps the value
 * text, so it changes nothing.
 */
export function encodingAfterCoercion(
  surface: DestinationEncoding,
  coercion: 'string' | 'number' | 'boolean' | undefined,
): DestinationEncoding {
  return coercion === 'number' || coercion === 'boolean' ? 'text' : surface;
}

/** What is wrong with a literal at a destination. */
export interface DestinationValueFinding {
  /**
   * **What kind of thing is wrong** — *derived from which rule matched*, never declared on the rule
   * (`D41`). The level is not here: it follows the kind, through `findingLevel` (`3i`, `#1412`), so
   * a constraint's *wrong, and you may proceed* and a sentinel's *valid, and probably not what was
   * meant* are decided once, in `FINDING_KINDS`, rather than at each `return`.
   *
   * The pill shows the kind's label and the banner shows the sentence, so a call site forwards the
   * kind verbatim instead of mapping it — a second decision about severity is a second place for
   * the surfaces to disagree about one value (`#1187`).
   */
  readonly kind: FindingKind;
  /**
   * What the destination declared, when the finding is **about the type**.
   *
   * Optional since `D41`: an enum, pattern, range or sentinel finding is not a type finding, and a
   * constraint is checkable on a field that declares no type at all.
   */
  readonly expected?: DestinationFieldType;
  /**
   * The operator-facing sentence — **shared, not re-written per surface**.
   *
   * Two surfaces explaining one decision in their own words is how one of them ends up describing
   * behaviour the other does not have, and a validation message is read at the moment somebody is
   * deciding whether to trust us (`#1187`).
   */
  readonly message: string;
}

/** A literal that is entirely or partly a macro cannot be graded until it resolves. */
const MACRO_DELIMITED = /[{[][^{}[\]]+[}\]]/;

/**
 * **How a sentence names each type** — exported because `expression-yield`'s shape finding says the
 * same thing about the same slot, and two phrasings of *a list* would be two sentences nothing checks
 * (`#1187`).
 */
export const DESTINATION_TYPE_PHRASE: Record<DestinationFieldType, string> = {
  string: 'text',
  number: 'a number',
  boolean: 'true or false',
  array: 'a list',
  object: 'an object',
};

/**
 * The literal, quoted back so the sentence is checkable — scalars only.
 *
 * `String(value)` on an `unknown` yields `[object Object]`, which would put a phrase in the
 * message that is not what the operator typed. A non-scalar is described by its *shape* instead.
 */
function showLiteral(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  return describeActual(value);
}

/** What we actually got, named the way an operator would name it. */
function describeActual(value: unknown): string {
  if (Array.isArray(value)) return 'a list';
  if (value !== null && typeof value === 'object') return 'an object';
  if (typeof value === 'number') return 'a number';
  if (typeof value === 'boolean') return 'true or false';
  return 'text';
}

/** A literal that reads as a finite number, whether it arrived as one or as text. */
function readsAsNumber(value: unknown): boolean {
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  return trimmed !== '' && Number.isFinite(Number(trimmed));
}

/**
 * **Text that is not a value yet** — blank, or still carrying a `{macro}` / `[macro]` token. Neither is
 * graded, because what reaches the wire is decided at request time. One predicate, so the body's
 * literal grading and this check exempt the same things.
 */
export function isDeferredText(value: string): boolean {
  return value.trim() === '' || MACRO_DELIMITED.test(value);
}

/**
 * **Does this authored literal match the type the destination declared?** `null` when there is
 * nothing to say — which is most of the time, and deliberately so.
 *
 * Four things return `null` before any comparison happens, and each is a rule rather than a
 * convenience:
 *
 * - **The field declares no type.** An unstated declaration says *nothing* — never *anything goes*
 *   and never *this is wrong* (`D34`). 148 catalog fields say nothing today.
 * - **There is no value.** Missing and invalid are one keystroke apart and the distinction is the
 *   whole of `R3a`: an empty box is unfinished, `10` in it is wrong. *Whether* a slot must be
 *   filled is `required + unfilled`'s question and is asked elsewhere.
 * - **The value is not a literal yet.** `{pod_max_dur}` is not gradeable until it resolves, which is
 *   why `#914` has two checkpoints — this one against literals at author time, and the payload
 *   actually sent at test time, where a macro that resolved to something unacceptable is finally
 *   visible. Grading a token here would report an error on every correct mapping.
 *
 *   **It has two spellings and this rule was written for one** (`#1198`, corrected 2026-08-28). A
 *   macro in a VAST parameter is a *string* carrying `{…}`; a mapping in an ORTB body is a **node**
 *   — `{ "$macro": "genre" }`, `{ "$expr": … }`. Only the string form was recognised, so every
 *   mapped body field was graded as though the node were the value, and a correct mapping reported
 *   *"content.genre expects text and this is an object"*. Thirteen of them on an ordinary demand
 *   source, all false, all on the surface `R3a` had just been extended to.
 *
 *   That is `D9` exactly: the rule was **true and too narrow**, so nothing contradicted it and no
 *   gate fired. The node spelling arrived after this sentence was written and the sentence did not
 *   follow it.
 * - **A structured type on a text destination.** *How* a list rides a query string is `sendAs`'s
 *   question (`D23`), not this one, and answering it here would be a second opinion on it.
 *
 * ## Which planes call it (`FD4`: *wherever an authored literal meets a declared destination*)
 *
 * | Plane | Encoding | Since |
 * | --- | --- | --- |
 * | VAST query parameters | `text` | `#1191` |
 * | ORTB body — literals, and each leaf's default | `json`, or `text` under a coercing `Type` | `#1191`; defaults `4b` |
 * | Response mapping — a `static` source and the row's default | `json`, or `text` under a coercing `Type` | `4b` |
 * | Request headers | — | **not yet** — the constraint half, against a header declaration, is `4b-ii` |
 *
 * The response plane was recorded here as unreachable — *what an operator authors there is a source
 * path* — which was true of the common row and not of the plane (`FIELD_DECLARATION_AND_VALIDATION_DESIGN`
 * §3.4): a `static` source and a default are literals, landing on a target that declares its type.
 *
 * A validator wired where it can never fire is worse than an unwired one — it reads as *this is
 * fine*, which is exactly the state `VAST_PARAM_CATALOG` was in with zero declared types.
 */
export function checkDestinationValue(
  field: Pick<DestinationField, 'name' | 'valueType' | 'value' | 'constraint' | 'readAs'> | undefined,
  value: unknown,
  encoding: DestinationEncoding,
  context: { readonly protocolVersion?: string } = {},
): DestinationValueFinding | null {
  if (!field) return null;
  if (value === undefined || value === null) return null;
  if (typeof value === 'string' && isDeferredText(value)) return null;
  // The node spelling of *not a literal yet* — see the docstring. Recognised through the shared
  // vocabulary rather than by looking for a `$` prefix, because a stray `$foo` on an ordinary
  // object is a real field and must still be graded.
  if (isOrtbBodyLeafNode(value)) return null;

  // **A slot with a format of its own is graded by it, not by its type** — see `readAs`. The
  // resolver reads the value the same way, so a value this accepts is one the runtime writes.
  if (field.readAs) {
    if (DESTINATION_VALUE_READERS[field.readAs](value) !== undefined) return null;
    return {
      kind: 'bad-format',
      message: `"${field.name}" expects ${DESTINATION_VALUE_FORMAT_PHRASE[field.readAs]}, and "${showLiteral(value)}" is not one, so it is never written here.`,
    };
  }

  const expected = field.valueType;

  // **The constraint sweep runs whether or not a type is declared, and that ordering is the
  // finding** (`D41`, `#1192`). This function opened `if (!field || !expected) return null`, so
  // keying the whole check on `type` would have silently disabled every enum, pattern, range and
  // sentinel on any field that declares no type — which today is **all 70** of the vendor presets'
  // fields (measured `#1090`, 2026-08-28). A gate switched off by an unrelated absence is the
  // *satisfied by doing less* shape one more time, and it would have read as *these values are
  // fine*.
  //
  // Type first, because a value of the wrong shape makes every other rule's message misleading:
  // telling somebody their list is not one of four permitted strings, when the real problem is that
  // they wrote a list where a string goes, describes the second-best finding.
  const typeFinding = expected ? checkType(field.name, expected, value, encoding) : null;
  if (typeFinding) return typeFinding;
  return checkConstraints(field, value, context);
}

/**
 * The rules that apply to this slot, most specific first — **never restated, always followed**.
 *
 * Three places can constrain one value and each is authoritative about a different thing, so they
 * are consulted rather than merged into a declaration somebody keeps in step:
 *
 * 1. **The slot's own** `constraint` — this receiver's rule.
 * 2. **The concept's**, where the slot declares it carries one (`value.from === 'derived'`). This is
 *    what makes `us_privacy` graded identically as a VAST query parameter and an ORTB body field
 *    without either catalog holding a copy — the parity `#914` asks for, satisfied by the link that
 *    already exists and is already gated.
 * 3. **A vocabulary we own** (`value.from === 'concept'`), whose members are version-scoped and
 *    derivable since `#1112`. Declaring an enum beside it would be two answers to one question, and
 *    the version-scoped one is the one that would go stale.
 */
function checkConstraints(
  field: Pick<DestinationField, 'name' | 'value' | 'constraint'>,
  value: unknown,
  context: { readonly protocolVersion?: string },
): DestinationValueFinding | null {
  // **A rule over a list applies to each member, never to the list** (`4a`, `#914`).
  //
  // `showLiteral` describes a non-scalar by its *shape*, which is right for a type finding and
  // wrong for every rule below: `cur: ["Dollars"]` would have been graded as the string `a list`
  // and reported as *"cur" accepts a three-letter ISO-4217 code, and "a list" is not one*. The
  // sentence names our own placeholder rather than what the operator typed — `#1045` inside the
  // checker — and it fires on the correct value `["USD"]` just as readily.
  //
  // It was latent rather than broken: `3.1` measured the mechanism complete against the **one**
  // declared constraint, which is a scalar concept. *A probe that supplies the inputs* — the
  // fixture proved the function for the shape it happened to carry, and the first `array` slot to
  // declare a rule (`cur`, `wlang`) is what makes the gap real. So the member loop lands **with**
  // the declarations rather than after them.
  const members = Array.isArray(value) ? value : [value];
  const inList = Array.isArray(value);

  if (field.value?.from === 'concept') {
    // The one evaluator, not a second read of `ProtocolConcept.members`: `conceptMembersFor` filters
    // to what this protocol version can actually express, so a 2.5 receiver is not told it accepts
    // `accompanying`.
    const accepted = conceptMembersFor(field as DestinationField, context);
    for (const member of members) {
      const literal = gradableLiteral(member);
      if (literal === null) continue;
      if (accepted.length > 0 && !accepted.some((m) => m.toLowerCase() === literal.toLowerCase())) {
        return {
          kind: 'not-allowed',
          message:
            `"${field.name}" accepts ${listOf(accepted)} here` +
            `${context.protocolVersion ? ` on ${context.protocolVersion}` : ''}, and "${literal}"` +
            `${inList ? ' in this list' : ''} is not one of them.`,
        };
      }
    }
  }

  const rules: ValueConstraint[] = [];
  if (field.constraint) rules.push(field.constraint);
  // The concept's grammar, reached through the link the slot already declares rather than copied.
  if (field.value?.from === 'derived') {
    const concept = catalogEntryFor(field.value.source)?.constraint;
    if (concept) rules.push(concept);
  }

  // Errors across every rule and every member before any warning, so a wrong value is never
  // reported as a merely suspicious one when both apply — *worst wins*, and the operator sees the
  // blocking half first. Member-major within each half, so the first bad entry in a list is the one
  // named.
  for (const member of members) {
    const literal = gradableLiteral(member);
    if (literal === null) continue;
    for (const rule of rules) {
      const finding = checkRule(field.name, rule, literal, inList);
      if (finding) return finding;
    }
  }
  for (const member of members) {
    const literal = gradableLiteral(member);
    if (literal === null) continue;
    for (const rule of rules) {
      const sentinel = rule.sentinels?.find((s) => s.value.toLowerCase() === literal.toLowerCase());
      if (sentinel) {
        return {
          kind: 'unusual-value',
          message: `"${field.name}" ${inList ? 'carries' : 'is set to'} "${literal}", which ${sentinel.means}`,
        };
      }
    }
  }
  return null;
}

/**
 * **The member's own literal, or `null` when there is nothing in it to grade.**
 *
 * The same four exemptions {@link checkDestinationValue} applies to a whole value, applied to one
 * member of a list — an empty string, a macro in either spelling, and anything that is not a
 * scalar. A grammar written for a scalar has nothing to say about an object, and saying something
 * anyway is how `#1198` reported thirteen correct mappings as errors.
 */
function gradableLiteral(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '' || MACRO_DELIMITED.test(trimmed)) return null;
    return trimmed;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}

/** `values`, `pattern` and `range` — the rules that make a value *wrong*. */
function checkRule(
  name: string,
  rule: ValueConstraint,
  literal: string,
  inList = false,
): DestinationValueFinding | null {
  // Where the value is a list, the sentence has to say *which* entry — the operator is looking at
  // several and only one of them is wrong.
  const where = inList ? ' in this list' : '';
  if (rule.values && rule.values.length > 0) {
    if (!rule.values.some((v) => v.toLowerCase() === literal.toLowerCase())) {
      return {
        kind: 'not-allowed',
        message: `"${name}" accepts ${listOf(rule.values)}, and "${literal}"${where} is not one of them.`,
      };
    }
  }
  if (rule.range) {
    const n = Number(literal);
    if (!Number.isFinite(n)) {
      return {
        kind: 'wrong-type',
        message: `"${name}" expects a number ${describeRange(rule.range)}, and "${literal}"${where} is not one.`,
      };
    }
    if ((rule.range.min !== undefined && n < rule.range.min) || (rule.range.max !== undefined && n > rule.range.max)) {
      return {
        kind: 'out-of-range',
        message: `"${name}" accepts a number ${describeRange(rule.range)}, and ${literal}${where} is outside it.`,
      };
    }
  }
  if (rule.pattern) {
    // **Bounded input, because this expression becomes operator-authored under `D37`.** A
    // catastrophically backtracking pattern is then a denial of service written from the authoring
    // surface. This is a bound rather than a solution; the solution belongs with the store.
    if (literal.length > MAX_GRADED_LENGTH) return null;
    if (!new RegExp(rule.pattern.source).test(literal)) {
      // The operator's outcome, never the expression (`#1045`): somebody who typed `yes` needs to
      // know what a CCPA string looks like, not what our regex is.
      return {
        kind: 'bad-format',
        message: `"${name}" expects ${rule.pattern.describes}, and "${literal}"${where} is not.`,
      };
    }
  }
  return null;
}

/** A value long enough that grading it is not worth the backtracking risk — see `pattern`. */
const MAX_GRADED_LENGTH = 512;

function describeRange(range: { readonly min?: number; readonly max?: number }): string {
  if (range.min !== undefined && range.max !== undefined) return `from ${range.min} to ${range.max}`;
  if (range.min !== undefined) return `of at least ${range.min}`;
  return `of at most ${range.max}`;
}

/** `a`, `b` or `c` — an operator reads a sentence, not an array. */
function listOf(values: readonly string[]): string {
  if (values.length === 1) return `"${values[0]}"`;
  const quoted = values.map((v) => `"${v}"`);
  return `${quoted.slice(0, -1).join(', ')} or ${quoted[quoted.length - 1]}`;
}

function checkType(
  name: string,
  expected: DestinationFieldType,
  value: unknown,
  encoding: DestinationEncoding,
): DestinationValueFinding | null {
  const wrong = (why: string): DestinationValueFinding => ({
    kind: 'wrong-type',
    expected,
    message: `"${name}" expects ${DESTINATION_TYPE_PHRASE[expected]}${why}`,
  });

  if (encoding === 'text') {
    // Everything is text here, so only a literal that cannot be *read* as the declared type is
    // wrong. `w=1080` typed into a box is the string `"1080"` and is exactly right.
    if (expected === 'number' && !readsAsNumber(value)) {
      return wrong(` and "${showLiteral(value)}" is not one, so the exchange has nothing usable here.`);
    }
    if (expected === 'boolean' && !/^(true|false|0|1)$/i.test(showLiteral(value).trim())) {
      return wrong(` and "${showLiteral(value)}" is neither.`);
    }
    return null;
  }

  // JSON: the literal's own type is what the receiver reads, so text that looks like a number is
  // still text.
  if (expected === 'array' && !Array.isArray(value)) {
    return wrong(` and this is ${describeActual(value)} — it goes out as one value, not a list.`);
  }
  if (expected === 'object' && (Array.isArray(value) || typeof value !== 'object')) {
    return wrong(` and this is ${describeActual(value)}.`);
  }
  if (expected === 'number' && typeof value !== 'number') {
    return readsAsNumber(value)
      ? wrong(` and this is text — an OpenRTB body sends it with the quotes, which is not the same value.`)
      : wrong(` and this is ${describeActual(value)}.`);
  }
  if (expected === 'boolean' && typeof value !== 'boolean') {
    return wrong(` and this is ${describeActual(value)}.`);
  }
  if (expected === 'string' && typeof value !== 'string') {
    return wrong(` and this is ${describeActual(value)}.`);
  }
  return null;
}

/* ------------------------------------------------------------------------- *
 * A reference is checked against what it yields (`FD11`, `#1413`)
 * ------------------------------------------------------------------------- */

/**
 * **What a row points at, in the two spellings a reference has** (`D24`, `FD11`).
 *
 * Both members are required and nullable rather than optional (`#1134`, applied to an argument):
 * a surface that carries one spelling and not the other says so, and one that forgot could not
 * compile. The alternative is the shape `#1254` measured — an optional argument silently answering
 * with less on three readers out of four.
 */
export interface AuthoredReference {
  /** A macro token, with or without its delimiters — `{ip}`, `ip`. `null` where the row has none. */
  readonly macro: string | null;
  /** A context path, as authored — `session.user.ipAddress`, `user.ipAddress`. `null` where none. */
  readonly path: string | null;
}

/**
 * **Every concept whose declared path this authored path names** — the source half of the check
 * below, and `null` is not one of its answers: an empty list is *no concept claims this path*.
 *
 * ## Why a list rather than an entry
 *
 * Three declared paths carry **two** concepts each (measured 2026-09-23):
 * `session.content.contentLength` is `content_length` and `content_duration`, `pod.maxDuration` is
 * `pod_max_dur` and `max_duration`, `pod.maxAds` is `pod_max_ads` and `pod_ads`. Each pair is one
 * value under two spellings, so a lookup that returned *an* entry would report a mismatch against
 * whichever of the two it did not happen to pick — a surface warning about correct behaviour
 * (`#1269`), on the three paths most likely to be authored by hand.
 *
 * ## Longest declared prefix, so a member of a value still names its concept
 *
 * `session.content.genres[0]` is one genre and names `genre`; a check that demanded equality would
 * call it a mismatch everywhere `genre` is expected. What that authored path *yields* — a string
 * rather than the list — is the **shape** question, which is `#1205`'s declaration and deliberately
 * not this (`FD11`).
 *
 * The bracket spelling and the root aliases are resolved through the declarations that own them
 * ({@link splitAuthoredPath}, {@link canonicalizeContextPath}) rather than restated here, in the
 * order `isKnownContextPath` uses: brackets first, so `content.genres[0]` canonicalises like its
 * dotted spelling.
 */
export function conceptsAtContextPath(path: string): readonly FieldCatalogEntry[] {
  const authored = splitAuthoredPath(canonicalizeContextPath(splitAuthoredPath(path).join('.')));
  if (authored.length === 0) return [];

  let matched: FieldCatalogEntry[] = [];
  let matchedLength = 0;
  for (const entry of FIELD_CATALOG) {
    if (!entry.path) continue;
    const declared = splitAuthoredPath(entry.path);
    if (declared.length > authored.length) continue;
    if (!declared.every((segment, i) => segment === authored[i])) continue;
    if (declared.length > matchedLength) {
      matched = [entry];
      matchedLength = declared.length;
    } else if (declared.length === matchedLength) {
      matched.push(entry);
    }
  }
  return matched;
}

/**
 * **Does this row point at the concept the destination says it carries?** `null` when there is
 * nothing to say, which is most of the time and deliberately so.
 *
 * {@link checkDestinationValue} grades a **literal** and exempts everything else, because *a macro
 * is not gradeable until it resolves*. That is true of a value and **false of a reference**
 * (`#1413`): a path or a macro is a declared thing pointing at another declared thing, and 52
 * destination slots (34 ORTB, 18 VAST) already say which concept they carry. So a mapping of the
 * viewer's IP address into `regs.ext.us_privacy` is checkable with nothing new declared, and was
 * silent on every surface.
 *
 * ## It keys on the concept, never on the path string
 *
 * `D24` says a macro is sugar for a path, and the data qualifies it (`FD11`): **16** concepts read
 * a fallback chain of which the declared path is one link (`#1077`), and **3** are produced by
 * bidBrain with no path at all. Both spellings *name a concept*, which is what the two ends are
 * compared on.
 *
 * ## Generous, because the alternative is a surface that warns about correct behaviour
 *
 * `E1` at a type check — **report only where both ends are declared and disagree**. A path no
 * concept claims, a macro the catalog does not know, a slot that declares no concept: each is
 * silence. And where a row carries both spellings, one of them naming the expected concept is
 * enough — the operator has it right somewhere, and guessing which slot reaches the wire is a
 * second answer to a question the driver already owns.
 *
 * **A `warn`, ruled by Todd 2026-09-22.** `R3a`'s tie-break — *if we are not sure, it is error, not
 * block* — is for *we cannot tell*. Here we can tell that the two concepts differ and cannot tell
 * that the operator is wrong to map them that way: `content_url` into `site.page` is defensible,
 * and a surface that errors on defensible authoring is the one that trains an operator to ignore
 * it. Nothing blocks a save either way.
 */
export function checkDestinationReference(
  field: Pick<DestinationField, 'name' | 'value'> | undefined,
  reference: AuthoredReference,
): DestinationValueFinding | null {
  if (!field || field.value?.from !== 'derived') return null;
  const expected = catalogEntryFor(field.value.source);
  if (!expected) return null;

  const named: Array<{ display: string; concepts: readonly FieldCatalogEntry[] }> = [];
  const macro = reference.macro?.trim();
  if (macro) {
    const entry = catalogEntryFor(macro);
    if (entry) named.push({ display: `{${macroTokenName(macro)}}`, concepts: [entry] });
  }
  const path = reference.path?.trim();
  if (path) {
    const concepts = conceptsAtContextPath(path);
    if (concepts.length > 0) named.push({ display: path, concepts });
  }

  if (named.length === 0) return null;
  if (named.some(({ concepts }) => concepts.some((c) => c.name === expected.name))) return null;

  const [{ display, concepts }] = named;
  return {
    kind: 'mismatch',
    message:
      `"${field.name}" is where the ${expected.description} goes, and this is mapped to ` +
      `"${display}" — the ${concepts[0].description}. Whatever it resolves to is what we send.`,
  };
}

// ---------------------------------------------------------------------------
// Inbound target roots (`X49`, `#1232`)
// ---------------------------------------------------------------------------

/**
 * **Where an inbound mapping may write** — the destination namespaces, declared once for the
 * resolver and the console (`X49`, ratified by Todd 2026-09-04, `XS3` slice 6).
 *
 * `#890` carried a fork open for two months: is `ad.bid` *what the catalogue displays* or *what the
 * store holds*. It is what the store holds. Display-only namespacing is a **lossless bijection
 * between the store and the screen**, which is `#936` — a translation that *"can never add
 * information and can only lose it"* — and this frame has spent `#1213`, `#1216` and `#1217`
 * removing exactly that shape from the mapping surfaces.
 *
 * It also closes a door already visible: a prefix that exists only for display **caps targets at the
 * ad object forever**, because there is nothing under it for a second namespace to sit beside.
 * `#1235`'s cardinality question needs *somewhere ad-scoped and somewhere above it*, so `pod` as a
 * root is the next question rather than a hypothetical — and the display-only answer would make
 * answering it a migration rather than an addition.
 *
 * ## This is a dispatch, not a rename
 *
 * The screen shows `ad.bid`, the store holds `ad.bid`, and the resolver dispatches on the first
 * segment — which is precisely what `resolveMacroPath` does for the **read** side (`parts[0] in ctx`).
 * The two sides of a mapping now name their namespaces the same way, which is the symmetry `#1000`
 * established for reading and nothing had established for writing.
 *
 * ## `ad` is a destination namespace and must never become a context root
 *
 * *Sources and destinations are two catalogs*, which is this file's own subject. `CONTEXT_ROOTS`
 * answers *where can a value come from* and must not gain an `ad` member on the strength of this.
 *
 * ## What `X49` got wrong about the code, corrected here because a canonical claim is load-bearing
 *
 * `X49` states that *"`applyResponseMapping` writes onto a `{ ad, pod }` root, and `['ad','bid']` is
 * the literal target"*, present tense. **It did not.** Measured 2026-09-04: it called
 * `setAtPath(ad, entry.target, value)`, so the literal target was the bare `['bid']` and there was no
 * root object at all. The conclusion is unaffected — it rested on the `#936` argument and on the
 * corollary's test, neither of which depends on that sentence — but the *work* was larger than the
 * ratification implied, and a design record asserting the code already does something is how the
 * next reader concludes there is nothing to build.
 */
export interface InboundTargetRoot {
  /** The canonical first segment of a stored target path. */
  readonly name: string;
  /** What an author is writing into. Operator-facing; it reaches the target picker. */
  readonly description: string;
  /**
   * **How long a value written under this root survives** — `X32` question 2, on the store side.
   *
   * Declared **on the root** rather than in a map keyed by root names, which is `#1190`'s rule: an
   * exclusion — or here a claim — belongs on the thing it is about, not in a parallel list that can
   * fall out of step with it. {@link PATH_LIFETIME}'s *root plus exceptions* shape is the model;
   * its **map** is not extended, because that one is keyed on **context roots** and *sources and
   * destinations are two catalogs*.
   */
  readonly lifetime: ValueLifetime;
  /** Why that lifetime, in terms of what actually carries the value. Reaches no operator surface. */
  readonly lifetimeReason: string;
  /**
   * **How many of these there are per response** — cardinality on the store side (`X56a`, `#1296`).
   *
   * `'ad'` says there is one of these for each returned ad, so a row written here is written N
   * times and each write lands on a different object. `null` says there is **one** for the whole
   * response — which is where `X56c` lives: a surface that runs once per ad, writing into a single
   * slot, has to say what happens to the other N, and *append* is the answer `#918` gives from the
   * outbound side. Nothing declares `null` today; `#1235`'s `pod` root will be the first, and
   * declaring the field now is what makes that an **addition** rather than a re-pick.
   *
   * It is the destination half of {@link ContextFanOut} and shares its vocabulary, not its
   * catalogue — *sources and destinations are two catalogs*, and this is the same question asked
   * of a place rather than of a value.
   */
  readonly onePer: FanOut | null;
  /**
   * Paths beneath this root whose lifetime differs from the root's default. Empty is a real answer
   * and is the answer today — declared rather than omitted, so *nobody looked* and *there are none*
   * do not collapse.
   */
  readonly lifetimeExceptions: readonly TargetLifetimeException[];
  /**
   * **The narrower reader**, where this root has one — `X32` question 3, collapsed.
   *
   * `undefined` means everything stored here reaches everything that reads here, so there is
   * nothing to say. See {@link OptimizerProjection}.
   */
  readonly optimizerProjection?: OptimizerProjection;
}

/** A target path whose lifetime differs from its root's default. */
export interface TargetLifetimeException {
  readonly path: string;
  readonly lifetime: ValueLifetime;
  readonly reason: string;
}

/**
 * **Which fields the optimizer selects on, where that is narrower than what is stored** (`X32`
 * question 3 as ratified by Todd, 2026-09-04, `#1248`).
 *
 * ## Question 3 is question 2, plus this one exception
 *
 * `X32` originally asked *will it change what I expect it to* and predicted the answer *nothing
 * reads it yet* — a value authored, stored and inert, of which `#1215` is the instance. Measuring
 * it produced a different answer: the value **is** read, by a **different reader than the operator
 * expects**. Todd: *"outside of the optimizer, I don't know that the answer is any different [from]
 * the lifetime."* So there is one boolean here and lifetime answers the rest.
 *
 * ## And *"read"* was the wrong word
 *
 * `X32` used *read* for question 1 (*can I read it here*) and again for question 3 (*who else reads
 * this*), which is `X3` inside a vocabulary of three marks. Todd: *"I think what the user wants to
 * know is 'where will/can this be used' — read is a tech detail."* The operator-facing sentences
 * below say **used**, and never *read*.
 *
 * ## Why it is a property of this destination and not a third grain question
 *
 * Open question, answered here rather than left: it is declared on the **root** because it is a
 * fact about *this namespace having two readers*, not a question every surface asks of every path.
 * The other mapping surfaces' destinations are keys on a wire — a header, a query parameter, an
 * ORTB body path — and the receiver is the exchange, so *which reader* has no meaning there. This
 * root is the only destination that is an **object** somebody selects from. A second root with a
 * narrowing projection declares its own, which is an addition; promoting it to a third question
 * asked of every surface would be a change to a design nothing has asked for.
 *
 * ## It is a hand-kept list, so it is gated
 *
 * `destination-field.ts` is aliased into the console and its import subgraph is closed, so it
 * cannot import `vast.dto.ts` to derive this. `scripts/response-target-catalog.spec.ts` reads
 * `BidRefDto` and `VastAdDto` with the TypeScript compiler API and fails when the projection here
 * and the projection in code disagree — in **both** directions, since a check that only asked
 * *is everything declared here in `BidRefDto`* is satisfied by a shorter list.
 */
export interface OptimizerProjection {
  /** The type that decides the set, so the gate and the reader name the same thing. */
  readonly declaredBy: string;
  /** Fully-qualified target paths the optimizer selects on. */
  readonly paths: readonly string[];
  /** Why the set is narrower than the root, in terms of what carries it. */
  readonly reason: string;
}

export const INBOUND_TARGET_ROOTS: readonly InboundTargetRoot[] = [
  {
    name: 'ad',
    description: 'The ad being extracted — every field bidBrain carries about one returned creative',
    lifetime: 'break',
    lifetimeReason:
      'The full `VastAdDto` goes to the shared bid cache at `bid:{contentHash}` and is hydrated at serve; `PodDto.cachedBidRefs` is "accumulated across fetch rounds" and carries it between them. A pod is one ad break, so an extracted value survives every round of this break and is rebuilt for the next.',
    onePer: 'ad',
    lifetimeExceptions: [],
    optimizerProjection: {
      declaredBy: 'BidRefDto',
      // Every `BidRefDto` member that is also a `VastAdDto` field. Three of these
      // (`contentHash`, `demandSource`, `score`) are declared out as targets in the console's
      // catalogue — declared here anyway, because this states what the **projection** carries,
      // not what the picker offers. Tying the two together would make a catalogue edit silently
      // move the projection.
      paths: [
        'ad.adId',
        'ad.contentHash',
        'ad.bid',
        'ad.netValue',
        'ad.durationSeconds',
        'ad.demandSource',
        'ad.iabCategory',
        'ad.adDomain',
        'ad.score',
        'ad.seat',
        'ad.dealId',
      ],
      reason:
        'The optimizer selects from `PodDto.cachedBidRefs`, which holds `BidRefDto` — a slim projection of the ad, kept small on purpose so a pod entry in Redis never carries full creatives. Everything else survives in the bid cache and is hydrated at serve.',
    },
  },
] as const;

const TARGET_ROOT_BY_NAME = new Map(INBOUND_TARGET_ROOTS.map((r) => [r.name, r]));

/** Longest match wins, so a nested exception added later is an addition rather than a silent change. */
function bestTargetException(
  under: string,
  exceptions: readonly TargetLifetimeException[],
): TargetLifetimeException | undefined {
  let best: TargetLifetimeException | undefined;
  for (const exception of exceptions) {
    const matches = under === exception.path || under.startsWith(`${exception.path}.`);
    if (matches && (!best || exception.path.length > best.path.length)) best = exception;
  }
  return best;
}

/**
 * **How long a value written to this target survives, and why** — `X32` question 2 on the store
 * side, and the sibling of `pathLifetime` on the read side.
 *
 * Takes either spelling, since a stored target may still be pre-`X49` (`canonicalizeInboundTarget`).
 * `undefined` for a target under no declared root — *nobody declared this* must not read as *this
 * survives nothing*, the same rule `knowableAt` and `runtimeUndecided` follow.
 */
export function targetLifetime(target: string): { lifetime: ValueLifetime; reason: string } | undefined {
  const [root, ...rest] = canonicalizeInboundTarget(target.split('.').filter(Boolean)).map(String);
  const entry = root === undefined ? undefined : TARGET_ROOT_BY_NAME.get(root);
  if (!entry) return undefined;
  const exception = bestTargetException(rest.join('.'), entry.lifetimeExceptions);
  return exception
    ? { lifetime: exception.lifetime, reason: exception.reason }
    : { lifetime: entry.lifetime, reason: entry.lifetimeReason };
}

/**
 * **How many of these a response produces** — the store-side sibling of `sharedAcrossFanOut`.
 *
 * `undefined` for a target under no declared root, which is *nobody declared this* and never *there
 * is one of them* — the same rule `targetLifetime` follows.
 */
export function targetCardinality(target: string): { onePer: FanOut | null } | undefined {
  const canonical = canonicalizeInboundTarget(target.split('.').filter(Boolean)).map(String);
  const entry = TARGET_ROOT_BY_NAME.get(canonical[0]);
  return entry ? { onePer: entry.onePer } : undefined;
}

/**
 * **Does the optimizer see a value written here?** — `X32` question 3, one boolean.
 *
 * `undefined` where the root declares no projection, which is *nothing to say* rather than *no*.
 * `true` is the marked case and the sentence that renders it is {@link USED_BY_OPTIMIZER}; `false` is
 * the silent one, because *stored on the ad and served* is what an author already assumes.
 */
export function usedByOptimizer(target: string): boolean | undefined {
  const canonical = canonicalizeInboundTarget(target.split('.').filter(Boolean)).map(String);
  const entry = TARGET_ROOT_BY_NAME.get(canonical[0]);
  const projection = entry?.optimizerProjection;
  if (!projection) return undefined;
  const path = canonical.join('.');
  return projection.paths.some((p) => path === p || path.startsWith(`${p}.`));
}

/**
 * **The short mark**, for a type-down `<option>` — which renders its value and its text content and
 * nothing else, so a mark there is a sentence or it is nothing.
 *
 * It names the consequence and not the mechanism (`#1045`), and it says *used* rather than *read*.
 *
 * ## It marks the eight, and it used to mark the sixteen (Todd, 2026-09-08, `#1259`)
 *
 * The rule did not change and is not being reversed: **the mark corrects an assumption, so it goes
 * on the case that contradicts one.** What changed is the input — *which* assumption — and that is
 * the operator's fact, not ours to derive:
 *
 * > *"From the author's perspective, anything they are mapping to the ad object is what gets stored
 * > on the `VastAdDto` for that ad."* (Todd, `#1248`)
 *
 * So the baseline an author brings to this surface is **extracted and stored on the ad**, which is
 * true of all twenty-four; the fact that contradicts nothing is *served*, and the one that adds
 * something is *this one also chooses which ads run*. Marking the sixteen restated the baseline on
 * two rows out of three, which buries the mark that varies — Q1's own rule, arriving from the other
 * side.
 *
 * **It was decided by counting the wrong thing.** `#1248` argued the sixteen from *an operator
 * assumes what they map is used*, asserted rather than measured, and the person who could settle it
 * settled it four days later by using the surface. **Count the assumption, and where the assumption
 * is about what an operator brings, that is a question to ask rather than a number to derive.**
 */
export const USED_BY_OPTIMIZER = 'used to choose which ads run';

/**
 * The same fact with room to be honest about it, for a `title` or a row mark.
 *
 * **It must never read as *and nothing else can be*.** Todd: `BidRefDto` is *"specialized,
 * controlled, and intentionally small — authors can create custom fields that are added, but it's
 * strongly discouraged"*. So the true sentence names what this set is and that widening it is a
 * decision, which is a different thing from a refusal — the same bound the negative sentence
 * carried, kept when the mark flipped.
 */
export const USED_BY_OPTIMIZER_DETAIL =
  'The optimizer selects on this one. It reaches ad selection as well as the served ad, because it ' +
  'is in the deliberately small set of fields a pod entry carries between rounds — widening that ' +
  'set is a decision rather than a mapping.';

/** Is this segment a declared inbound target namespace? */
export function isInboundTargetRoot(segment: unknown): boolean {
  return typeof segment === 'string' && INBOUND_TARGET_ROOTS.some((r) => r.name === segment);
}

/**
 * **The one place the pre-`X49` spelling is read** — the `X3` pattern (`#1216`).
 *
 * A stored target with no leading root segment is **ad-relative**, and there is no ambiguity to
 * resolve: `ad` was never a legal first segment before this, so a bare `['bid']` can only be the old
 * spelling. Nothing rewrites a stored document until it is next saved, and a **write emits one
 * spelling** — two answers on one target is the defect being closed.
 *
 * The driver default documents are **ours**, so they are rewritten rather than left to this: a seed
 * we ship carrying the retired spelling is the one instance nobody ever saves and therefore never
 * converts.
 */
export function canonicalizeInboundTarget(target: readonly (string | number)[]): (string | number)[] {
  if (target.length === 0) return [];
  return isInboundTargetRoot(target[0]) ? [...target] : ['ad', ...target];
}

/**
 * **The whole ad, offered or declared out** — `X47` step 1 and `X49` (`#1232`, `XS3` slice 6).
 *
 * This was **nine flat fields** against `VastAdDto`'s **31**, under a header reading *Session
 * Context*, and nobody decided nine — it is the set that was written when the file was created.
 * Todd, rehearsing slice 5: *"it should provide full access to the object model (it's the 'where I
 * put it') — currently it's only offering the ones we already parse."*
 *
 * It is the `#1217` shape on the **destination** side, and the same rule that fixed the source side
 * applies: *the widest scope that meets the criteria*, so a field is left out only when writing it
 * would be **wrong**, never because nobody got round to describing it.
 *
 * ## Paths are real, not decorated
 *
 * `ad.bid` is what is **stored** (`X49`), not a prefix put on for display — the resolver dispatches
 * on the first segment exactly as `resolveMacroPath` does for reads. A bare `bid` is the pre-`X49`
 * spelling, canonicalised on the way in; nothing rewrites a stored document until it is next saved.
 *
 * ## An exclusion sits on the thing it excludes
 *
 * `#1190`: a decline the mechanism cannot read is a comment, and it survives exactly until somebody
 * improves the mechanism. So a withheld field is an entry here with its reason, not an absence — and
 * `scripts/response-target-catalog.spec.ts` fails when `VastAdDto` gains a field that is neither
 * offered nor declared out, which is the direction *a gate satisfied by doing less* cannot reach.
 *
 * ## It is a `DestinationField`, and it lives here (`FD1`, `4b`)
 *
 * It was the **fourth** destination shape — `MappingFieldDescriptor` with a local `notOffered` — and
 * escaped `D34`'s count because it sits on the inbound side, where *destination* reads as wrong. The
 * value is written to `ad.bid` all the same, and `ad.bid` declares a type, an importance and a
 * description. As one, a `static` literal or a `default` written here is graded by
 * {@link checkDestinationValue} like any other, which is `FD4`'s response half.
 *
 * **And it moved out of the console** because the resolver refuses what this declines (`#1320`), and
 * the two were two lists: the resolver's `WITHHELD_AD_TARGETS` and this one, held equal by a spec
 * reading the console's list out of source text. Both now read this.
 */
export const AD_TARGET_FIELDS: readonly DestinationField[] = [
  // ── optimizer-critical ──────────────────────────────────────────────────
  // **`recommended`, never `required`** (Todd, 2026-09-23). *"Used by optimizer isn't the same as
  // required like we use it elsewhere"* — `required` is `X46`'s claim that a destination will not
  // work without a value, and coverage of these in real responses is expected to be sparse. The
  // driver already extracts each of them; what the optimizer needs is `OPTIMIZER_CRITICAL_TARGETS`
  // and `usedByOptimizer`, not this field.
  { name: 'ad.bid', valueType: 'number', importance: 'recommended', description: 'Bid / CPM (optimizer-critical)' },
  {
    name: 'ad.durationSeconds',
    valueType: 'number',
    importance: 'recommended',
    description: 'Ad duration in seconds (optimizer-critical)',
    readAs: 'duration-seconds',
  },
  {
    name: 'ad.adDomain',
    valueType: 'string',
    importance: 'recommended',
    description: 'Advertiser domain (optimizer-critical)',
  },
  {
    name: 'ad.iabCategory',
    valueType: 'string',
    importance: 'recommended',
    description: 'IAB category (optimizer-critical)',
  },
  {
    name: 'ad.adId',
    valueType: 'string',
    importance: 'recommended',
    description: 'Ad / creative ID (optimizer-critical)',
  },

  // ── the creative ────────────────────────────────────────────────────────
  {
    name: 'ad.mediaUrl',
    valueType: 'string',
    importance: 'recommended',
    description: 'Media URL — the video file or streaming manifest',
  },
  { name: 'ad.mediaFormat', valueType: 'string', importance: 'optional', description: 'MIME type, e.g. video/mp4' },
  { name: 'ad.duration', valueType: 'string', importance: 'optional', description: 'Duration as HH:MM:SS' },
  { name: 'ad.width', valueType: 'number', importance: 'optional', description: 'Creative width in pixels' },
  { name: 'ad.height', valueType: 'number', importance: 'optional', description: 'Creative height in pixels' },
  { name: 'ad.skipOffset', valueType: 'string', importance: 'optional', description: 'Skip offset as HH:MM:SS' },
  {
    name: 'ad.isWrapper',
    valueType: 'boolean',
    importance: 'optional',
    description: 'The response was a VAST <Wrapper> — no media or duration until the redirect is followed',
  },
  {
    name: 'ad.rawMarkup',
    valueType: 'string',
    importance: 'optional',
    description: "The original ad markup, served back with bidBrain's injections rather than reconstructed",
  },

  // ── tracking ────────────────────────────────────────────────────────────
  { name: 'ad.impressions', valueType: 'array', importance: 'optional', description: "The DSP's impression URLs" },
  {
    name: 'ad.tracking',
    valueType: 'object',
    importance: 'optional',
    description: 'Tracking URLs, keyed by event name',
  },
  { name: 'ad.clickThrough', valueType: 'string', importance: 'optional', description: 'Click-through landing URL' },
  { name: 'ad.error', valueType: 'string', importance: 'optional', description: 'Error tracking URL' },
  {
    name: 'ad.winNoticeUrl',
    valueType: 'string',
    importance: 'optional',
    description: 'Win-notice URL (fired on win)',
  },
  {
    name: 'ad.billingNoticeUrl',
    valueType: 'string',
    importance: 'optional',
    description: 'Billing-notice URL (injected into VAST)',
  },

  // ── commercial and attribution ──────────────────────────────────────────
  { name: 'ad.dealId', valueType: 'string', importance: 'optional', description: 'PMP deal ID' },
  { name: 'ad.seat', valueType: 'string', importance: 'optional', description: 'Bidder seat' },

  // ── declared out ────────────────────────────────────────────────────────
  // **Refused where the mapping resolves, not only here** (`#1320`, slice `2a`): the resolver keeps the
  // same list (`WITHHELD_AD_TARGETS`), and `response-mapping.resolver.spec.ts` holds the two equal.
  {
    name: 'ad.netValue',
    valueType: 'number',
    importance: 'optional',
    description: "Bid minus the demand source's cost",
    notOffered:
      "bidBrain computes it from the bid and this source's cost — a mapped value would replace that calculation",
  },
  {
    name: 'ad.sourceIndex',
    valueType: 'number',
    importance: 'optional',
    description: "The ad's 1-based position in the response this source returned — not its pod position",
    notOffered: 'bidBrain assigns the position in the response it parsed',
  },
  {
    name: 'ad.extractedPixelData',
    valueType: 'object',
    importance: 'optional',
    description: 'Values extracted from pixels',
    notOffered: 'pixel extraction writes it, and nothing reads it yet — map onto a field on the ad instead',
  },
  {
    name: 'ad.podId',
    valueType: 'string',
    importance: 'optional',
    description: 'The pod this ad was returned into',
    notOffered: 'bidBrain assigns the pod — an ad belongs to the pod being built, not to what the exchange said',
  },
  {
    name: 'ad.cpm',
    valueType: 'number',
    importance: 'optional',
    description: 'Deprecated alias of bid',
    notOffered: 'deprecated — `bid` is the CPM, and two names for one value is the drift `X3` closes',
  },
  {
    name: 'ad.demandSource',
    valueType: 'string',
    importance: 'optional',
    description: 'Which demand source returned this ad',
    notOffered:
      'bidBrain knows which source it asked — an extraction claiming otherwise misattributes every metric row',
  },
  {
    name: 'ad.score',
    valueType: 'number',
    importance: 'optional',
    description: 'Optimization score',
    notOffered: 'computed by the scoring rules after extraction, so anything written here is overwritten',
  },
  {
    name: 'ad.contentHash',
    valueType: 'string',
    importance: 'optional',
    description: 'Dedup hash',
    notOffered: 'derived from the other fields by computeAdContentHash — authoring it breaks deduplication',
  },
  {
    name: 'ad.injectedImpressions',
    valueType: 'array',
    importance: 'optional',
    description: "bidBrain's own impression pixels",
    notOffered:
      "bidBrain's own additions, kept apart from the DSP's `impressions` so the passthrough path does not double-fire them",
  },
  {
    name: 'ad.injectedTracking',
    valueType: 'object',
    importance: 'optional',
    description: "bidBrain's own tracking URLs",
    notOffered: 'same reason as injectedImpressions — mixing them double-fires the originals',
  },
] as const;

/**
 * **The ad field a target writes**, in either spelling — `undefined` for a target this does not declare.
 */
export function adTargetField(target: string): DestinationField | undefined {
  const canonical = canonicalizeInboundTarget(target.split('.').filter(Boolean)).join('.');
  return AD_TARGET_FIELDS.find((f) => f.name === canonical);
}

/** A withheld field is still a **known** target, so it is described rather than reported unknown. */
export function targetNotOfferedReason(target: string): string | undefined {
  return adTargetField(target)?.notOffered;
}

/**
 * **The headers the driver writes itself, and the concept each one carries** (`FD2` as widened by
 * Todd, 2026-09-23, slice `4b-ii`).
 *
 * `FD2` found no header catalogue of ours to write — no preset declares an auth header, and the rest
 * are the operator's. The driver's own headers are the exception, and they are ours by `#1353`'s
 * ruling: `buildRequestHeaders` writes `User-Agent` and `X-Forwarded-For` from the same context the
 * body reads (`resolveUserAgent`, `forwardedForHeader` — `#927`, `#1006`), as the driver's statement
 * of its own request. Declaring them is what lets a header take part in everything a destination's
 * `derived` link already drives: the request's conflict check (`#944`), the value grade (`FD4`), and
 * the concept check on what a row points at (`4d`).
 *
 * **Only the viewer headers.** `Content-Type` and the ORTB version header are the driver's too, and
 * carry no session concept, so declaring them would link nothing — and a declaration of ours standing
 * for the protocol's headers generally is the module `FD2` rejected. `type` is omitted on purpose: a
 * header value is text, and the constraint sweep runs without it (`D41`).
 *
 * `driver-header-fields.spec.ts` holds this to what both drivers send, in both directions.
 */
export const DRIVER_HEADER_FIELDS: readonly DestinationField[] = [
  {
    name: 'User-Agent',
    importance: 'optional',
    description: "The viewer's user agent — the driver sends it from the session unless a row overrides it",
    value: { from: 'derived', source: 'ua' },
  },
  {
    name: 'X-Forwarded-For',
    importance: 'optional',
    description: "The viewer's IP address — the driver sends it from the session unless a row overrides it",
    value: { from: 'derived', source: 'ip' },
  },
] as const;

/** The driver header of this name, compared case-insensitively as HTTP does. */
export function driverHeaderField(name: string): DestinationField | undefined {
  const lower = name.trim().toLowerCase();
  return DRIVER_HEADER_FIELDS.find((f) => f.name.toLowerCase() === lower);
}
